import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { GripVertical, RefreshCcw, Search, UserPlus } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  PageContainer, PageHeader, StatusPill, LEAD_STATUS, waitingLabel, relativeTime, Skeleton,
} from "./bdcUi";

// Kanban columns — matches Phase 11 backend LEAD_TRANSITIONS.
const COLUMNS = [
  { key: "new", label: "New", tone: "slate", accent: "border-slate-300" },
  { key: "contacted", label: "Contacted", tone: "blue", accent: "border-blue-300" },
  { key: "followed_up", label: "Followed Up", tone: "emerald", accent: "border-emerald-300" },
  { key: "lost", label: "Lost", tone: "slate", accent: "border-slate-400" },
  { key: "spam", label: "Spam", tone: "rose", accent: "border-rose-400" },
];

// Backend blocks moving OUT of lost/spam except via admin override; we hide the
// drag handle for those cards to keep UX honest.
const CLOSED_STATUSES = new Set(["lost", "spam"]);
const NOTES_REQUIRED = new Set(["lost", "spam"]);

const BdcQueue = () => {
  const { user } = useAuth();
  const [assignment, setAssignment] = useState("mine");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  // Drag state
  const [dragging, setDragging] = useState(null); // lead
  const [dragOverCol, setDragOverCol] = useState(null);

  // Mandatory-notes modal for lost / spam
  const [closeModal, setCloseModal] = useState(null); // { lead, targetStatus }
  const [closeReason, setCloseReason] = useState("");
  const [committing, setCommitting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { assignment, sort: "recent_activity", page: 1, page_size: 200 };
      if (debouncedQ) params.q = debouncedQ;
      const { data } = await api.get("/bdc/leads", { params });
      setLeads(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [assignment, debouncedQ]);

  useEffect(() => { load(); }, [load, refreshTick]);

  const columnsData = useMemo(() => {
    const byCol = Object.fromEntries(COLUMNS.map((c) => [c.key, []]));
    leads.forEach((l) => {
      if (byCol[l.status]) byCol[l.status].push(l);
    });
    return byCol;
  }, [leads]);

  const claim = async (lead) => {
    setClaiming(lead.id);
    try {
      await api.post(`/bdc/leads/${lead.id}/claim`);
      toast.success(`Claimed ${lead.customer?.full_name || lead.guest_name || "lead"}`);
      setRefreshTick((n) => n + 1);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setClaiming(null); }
  };

  const isDraggable = (lead) => (
    lead.assigned_bdc_id === user?.id && !CLOSED_STATUSES.has(lead.status)
  );

  const applyStatus = async (lead, targetStatus, reason) => {
    setCommitting(true);
    const body = { status: targetStatus };
    if (reason) body.close_reason = reason.trim();
    try {
      await api.patch(`/bdc/leads/${lead.id}`, body);
      toast.success(`Moved to ${LEAD_STATUS[targetStatus].label}`);
      // Optimistic local update
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, status: targetStatus, close_reason: reason || l.close_reason } : l)));
    } catch (e) {
      toast.error(formatApiError(e));
      setRefreshTick((n) => n + 1);
    } finally { setCommitting(false); }
  };

  const handleDrop = async (targetStatus) => {
    const lead = dragging;
    setDragging(null); setDragOverCol(null);
    if (!lead) return;
    if (lead.status === targetStatus) return;
    if (!isDraggable(lead)) {
      toast.error("You can only move leads assigned to you");
      return;
    }
    if (NOTES_REQUIRED.has(targetStatus)) {
      setCloseModal({ lead, targetStatus });
      setCloseReason("");
      return;
    }
    await applyStatus(lead, targetStatus);
  };

  const submitCloseReason = async () => {
    if (!closeModal || !closeReason.trim()) return;
    await applyStatus(closeModal.lead, closeModal.targetStatus, closeReason);
    setCloseModal(null); setCloseReason("");
  };

  return (
    <PageContainer>
      <PageHeader
        title="Lead board"
        subtitle="Drag leads across columns. Marking Lost or Spam requires a reason."
        testId="bdc-queue-header"
        right={
          <Button variant="outline" onClick={() => setRefreshTick((n) => n + 1)} data-testid="bdc-queue-refresh">
            <RefreshCcw size={15} /> Refresh
          </Button>
        }
      />

      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4 rounded-xl bg-white border border-slate-200 p-3">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search customer name, email, note…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="bdc-queue-search" />
        </div>
        <Tabs value={assignment} onValueChange={setAssignment}>
          <TabsList data-testid="bdc-queue-tabs">
            <TabsTrigger value="mine" data-testid="bdc-tab-mine">Mine</TabsTrigger>
            <TabsTrigger value="unclaimed" data-testid="bdc-tab-unclaimed">Unclaimed</TabsTrigger>
            <TabsTrigger value="all_bdc" data-testid="bdc-tab-all">All</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          {COLUMNS.map((c) => <Skeleton key={c.key} className="h-64" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="bdc-kanban-board">
          {COLUMNS.map((col) => {
            const items = columnsData[col.key] || [];
            const isOver = dragOverCol === col.key;
            return (
              <div
                key={col.key}
                className={`rounded-2xl border-2 bg-slate-50/60 transition-colors ${isOver ? `${col.accent} bg-white` : "border-slate-200"}`}
                onDragOver={(e) => { e.preventDefault(); setDragOverCol(col.key); }}
                onDragLeave={() => setDragOverCol((c) => (c === col.key ? null : c))}
                onDrop={(e) => { e.preventDefault(); handleDrop(col.key); }}
                data-testid={`bdc-kanban-col-${col.key}`}
              >
                <div className="px-3 pt-3 pb-2 flex items-center justify-between border-b border-slate-200/60">
                  <div className="flex items-center gap-2">
                    <StatusPill tone={col.tone}>{col.label}</StatusPill>
                    <span className="text-xs text-slate-500 font-semibold" data-testid={`bdc-kanban-count-${col.key}`}>{items.length}</span>
                  </div>
                </div>
                <div className="p-2 space-y-2 min-h-[240px]">
                  {items.length === 0 ? (
                    <p className="text-[11px] text-slate-400 text-center py-6 uppercase tracking-widest">Drop here</p>
                  ) : items.map((lead) => {
                    const name = lead.customer?.full_name || lead.guest_name || "Guest";
                    const email = lead.customer?.email || lead.guest_email;
                    const draggable = isDraggable(lead);
                    return (
                      <div
                        key={lead.id}
                        draggable={draggable}
                        onDragStart={() => setDragging(lead)}
                        onDragEnd={() => { setDragging(null); setDragOverCol(null); }}
                        className={`rounded-xl border border-slate-200 bg-white p-3 shadow-sm ${draggable ? "cursor-grab hover:shadow-md" : ""} ${dragging?.id === lead.id ? "opacity-40 rotate-1" : ""}`}
                        data-testid={`bdc-kanban-card-${lead.id}`}
                      >
                        <div className="flex items-start gap-2">
                          {draggable && <GripVertical size={14} className="text-slate-300 mt-0.5 shrink-0" />}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <Link to={`/bdc/leads/${lead.id}`} className="text-sm font-semibold text-slate-900 hover:text-brand truncate" data-testid={`bdc-kanban-link-${lead.id}`}>
                                {name}
                              </Link>
                              {!lead.assigned_bdc_id && (
                                <StatusPill tone="gold" className="text-[9px]">{waitingLabel(lead.created_at)}</StatusPill>
                              )}
                            </div>
                            <p className="text-[11px] text-slate-500 mt-0.5 truncate">
                              {lead.vehicle ? `${lead.vehicle.year} ${lead.vehicle.make} ${lead.vehicle.model}` : "—"}
                            </p>
                            <p className="text-[10px] text-slate-400 truncate">{email || "—"}</p>
                            <div className="flex items-center justify-between mt-2 gap-2">
                              <span className="text-[10px] text-slate-400">{relativeTime(lead.last_activity_at || lead.created_at)}</span>
                              {!lead.assigned_bdc_id ? (
                                <Button
                                  size="sm"
                                  className="h-6 px-2 text-[11px] bg-brand text-white hover:bg-brand-hover"
                                  onClick={() => claim(lead)}
                                  disabled={claiming === lead.id}
                                  data-testid={`bdc-kanban-claim-${lead.id}`}
                                >
                                  <UserPlus size={11} /> {claiming === lead.id ? "…" : "Claim"}
                                </Button>
                              ) : (
                                <Button asChild size="sm" variant="ghost" className="h-6 px-2 text-[11px]">
                                  <Link to={`/bdc/leads/${lead.id}`}>Open</Link>
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Mandatory notes when marking Lost / Spam */}
      <Dialog open={!!closeModal} onOpenChange={(v) => { if (!v) { setCloseModal(null); setCloseReason(""); } }}>
        <DialogContent data-testid="bdc-kanban-close-dialog">
          <DialogHeader>
            <DialogTitle>
              Mark as {closeModal ? LEAD_STATUS[closeModal.targetStatus].label : ""}
            </DialogTitle>
            <DialogDescription>
              Notes are required so the team understands why this lead was closed. This is captured in the audit log and shown on the lead detail.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Reason (required)</Label>
            <Textarea
              rows={4}
              value={closeReason}
              onChange={(e) => setCloseReason(e.target.value)}
              placeholder={closeModal?.targetStatus === "spam"
                ? "e.g. Numbers keep going to voicemail, obvious lead-gen spam"
                : "e.g. Customer bought elsewhere / not in market anymore"}
              data-testid="bdc-kanban-close-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCloseModal(null); setCloseReason(""); }}>Cancel</Button>
            <Button
              className={closeModal?.targetStatus === "spam" ? "bg-rose-600 hover:bg-rose-700 text-white" : "bg-slate-800 hover:bg-slate-900 text-white"}
              onClick={submitCloseReason}
              disabled={committing || !closeReason.trim()}
              data-testid="bdc-kanban-close-submit"
            >
              {committing ? "Saving…" : `Confirm ${closeModal ? LEAD_STATUS[closeModal.targetStatus].label : ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};

export default BdcQueue;
